import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, TextField, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import { useI18nContext, useI18nCountry } from "@hopper-b2b/i18n";
import { Passport } from "@b2bportal/air-booking-api";
import { CountryCode } from "@hopper-b2b/types";
import {
  getDateTimeWithFormat,
  specialCharacterRegex,
  parseYearMonthAndDay,
} from "@hopper-b2b/utilities";
import { Slot } from "../Slots";
import { renderMenu } from "./utils";
import "./styles.scss";
import emojiFlags from "emoji-flags";

export interface IPassportInfoLabels {
  countryOfIssue: string;
  passportNum: string;
  expiration: string;
}

export interface IPassportInfoFormProps {
  className?: string;
  isMobile?: boolean;
  title: string;
  subtitle: string;
  subtitle2?: string;
  passportInfo?: Passport;
  passportInfoLabels?: IPassportInfoLabels;
  onPassportInfoChange: (passportInfo: Passport) => void;
  passportExpiryError?: string;
  setPassportExpiryError: (error: string) => void;
  passportNumberError?: string;
  setPassportNumberError: (error: string) => void;
  departureDate?: Date | null | undefined;
  returnDate?: Date | null | undefined;
}

export const PassportInfoForm = ({
  title,
  subtitle,
  subtitle2,
  passportInfo,
  onPassportInfoChange,
  className,
  isMobile,
  passportInfoLabels,
  passportExpiryError,
  setPassportExpiryError,
  passportNumberError,
  setPassportNumberError,
  departureDate,
  returnDate,
}: IPassportInfoFormProps) => {
  const { t, brand } = useI18nContext();
  const { getAllCountryNames, getCountryName } = useI18nCountry();
  const [countryOfIssue, setCountryOfIssue] = useState<CountryCode>(
    passportInfo?.countryOfIssue ?? brand.preferredCountryCode
  );
  const [number, setPassportNum] = useState(passportInfo?.number || "");
  const [expiration, setExpiration] = useState(passportInfo?.expiration || "");

  const DEFAULT_PASSPORT_INFO_FORM_LABELS = {
    countryOfIssue: t("countryOfIssue"),
    passportNum: t("passportNumber"),
    expiration: `${t("expiration", {
      format: "YYYY-MM-DD",
      interpolation: { escapeValue: false },
    })}`,
  };

  const labels = passportInfoLabels
    ? passportInfoLabels
    : DEFAULT_PASSPORT_INFO_FORM_LABELS;

  useEffect(() => {
    if (number !== passportInfo?.number) {
      setPassportNum(passportInfo?.number || "");
    }
    if (expiration !== passportInfo?.expiration) {
      setExpiration(passportInfo?.expiration || "");
    }
    if (countryOfIssue !== passportInfo?.countryOfIssue) {
      setCountryOfIssue(
        passportInfo?.countryOfIssue || brand.preferredCountryCode
      );
    }
  }, [passportInfo]);

  const onPassportExpiryChanged = useCallback(
    (passportExpiry: string) => {
      const { year, month, day, strLength } =
        parseYearMonthAndDay(passportExpiry);

      const formattedPassportExpiry =
        year +
        (strLength > 4 ? "-" : "") +
        month +
        (strLength > 6 ? "-" : "") +
        day;

      onPassportInfoChange({
        countryOfIssue,
        number,
        expiration: formattedPassportExpiry,
      });
    },
    [countryOfIssue, number, onPassportInfoChange]
  );

  useEffect(() => {
    if (number) {
      if (/\s/.test(number)) {
        setPassportNumberError(t("noWhitespaceError"));
      } else if (!specialCharacterRegex.test(number)) {
        setPassportNumberError(t("noSpecialCharactersError"));
      } else {
        setPassportNumberError("");
      }
    }
  }, [number, setPassportNumberError, t]);

  useEffect(() => {
    if (expiration) {
      const expiryDateDayjs = getDateTimeWithFormat(expiration, "YYYY-MM-DD");
      const { month, day } = parseYearMonthAndDay(expiration);

      if (!expiryDateDayjs.isValid() || !expiryDateDayjs.isAfter(dayjs())) {
        setPassportExpiryError(t("enterAValidExpiry"));
      } else if (
        Number(month) !== expiryDateDayjs.month() + 1 ||
        Number(day) !== expiryDateDayjs.date()
      ) {
        setPassportExpiryError(t("enterAValidExpiry"));
      } else if (
        (returnDate &&
          expiryDateDayjs?.subtract(90, "day").isBefore(returnDate)) ||
        expiryDateDayjs?.subtract(90, "day").isBefore(departureDate)
      ) {
        setPassportExpiryError(t("passportExpires"));
      } else {
        setPassportExpiryError("");
      }
    }
  }, [departureDate, expiration, returnDate, setPassportExpiryError, t]);

  const countryOptions = useMemo(
    () =>
      Array.from(Object.keys(getAllCountryNames())).map((code) => {
        return {
          label: getCountryName(code),
          value: code,
          icon: (
            <span className="checkout-country-option-flag-icon">
              {emojiFlags.countryCode(code).emoji}
            </span>
          ),
        };
      }),
    [getAllCountryNames, getCountryName]
  );

  return (
    <Box
      className={clsx("passport-info-form-container", className, {
        mobile: isMobile,
      })}
    >
      <Box className="passport-info-form-description">
        <Typography variant="subtitle1" className="title">
          {title}
        </Typography>
        <Slot id="traveler-id-match-banner" />
        <Typography variant="subtitle2" className="subtitle">
          <span dangerouslySetInnerHTML={{ __html: subtitle }} />
        </Typography>
        {subtitle2 && (
          <Typography variant="subtitle2" className="subtitle2">
            {subtitle2}
          </Typography>
        )}
      </Box>
      <form className={clsx("passport-info-form", className)}>
        <TextField
          label={labels.countryOfIssue}
          className="passport-info-field"
          required={true}
          select={true}
          value={countryOfIssue}
          onChange={(event) => {
            setCountryOfIssue(event.target.value);
            onPassportInfoChange({
              countryOfIssue: event.target.value,
              number,
              expiration,
            });
          }}
        >
          {renderMenu(countryOptions)}
        </TextField>
        <TextField
          label={labels.passportNum}
          className={clsx("passport-info-field", "passport-number-field", {
            error: !!passportNumberError,
          })}
          value={number}
          onChange={(event) => {
            setPassportNum(event.target.value);
            onPassportInfoChange({
              countryOfIssue,
              number: event.target.value,
              expiration,
            });
          }}
          error={!!passportNumberError}
          helperText={passportNumberError}
        />
        <TextField
          label={labels.expiration}
          className={clsx("passport-info-field", "expiration-field", {
            error: !!passportExpiryError,
          })}
          value={expiration}
          onChange={(event) => {
            setExpiration(event.target.value);
            onPassportExpiryChanged(event.target.value);
          }}
          error={!!passportExpiryError}
          helperText={passportExpiryError}
        />
      </form>
    </Box>
  );
};
